import {
  AdElementDataSourceSchema,
  AdElementSchema,
  DateTimeFormatSchema,
  Datasources,
  isPinnedBackgroundLayer,
  type ElementReorderCommand,
  DiplayOptionsTeamAssetSchema,
  DisplayOptionsOddsFormatSchema,
  PenaltiesFormatSchema,
  type AdElement,
  type AdTemplateDisplayOptions,
} from '@adflow/types';

import { requestTemplateSave } from '../save/saveRequest';
import { dispatchTemplateOperationAudit } from './auditDispatch';
import { createOperationInvocationId } from './invocation';
import { type TemplateBuilderOperation, operationSupported } from './names';
import { applyAdTemplateToEditorStore } from './persistedTemplateHydration';
import { readPersistedRevision, readWorkingRevision } from './revision';
import type { AdTemplate } from '@adflow/types';
import type {
  ExecuteTemplateBuilderOperationInput,
  TemplateOperationAdvertiser,
  TemplateOperationContext,
  TemplateOperationErrorCode,
  TemplateOperationResult,
  TemplateOperationSuccessData,
} from './types';
import { validateImageContentWrite } from './validators';

const WIDGET_LAYER_MESSAGE =
  'Widget layers cannot be added, removed or have their data changed with this tool.';

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function advertiserRecordId(record: TemplateOperationAdvertiser): number | null {
  const id = record._id;
  if (typeof id === 'number' && Number.isFinite(id)) {
    return id;
  }
  if (typeof id === 'string' && id !== '') {
    const parsed = Number(id);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

type DisplayOptionsKey = keyof AdTemplateDisplayOptions;

const ELEMENT_FORMAT_DISPLAY_KEY: Record<
  'set_element_date_format' | 'set_element_odds_format' | 'set_element_penalties_format',
  DisplayOptionsKey
> = {
  set_element_date_format: 'dateTimeFormat',
  set_element_odds_format: 'odds',
  set_element_penalties_format: 'penaltiesFormat',
};

function isDisplayOptionsKey(key: unknown): key is DisplayOptionsKey {
  return (
    key === 'teamAssets' || key === 'odds' || key === 'dateTimeFormat' || key === 'penaltiesFormat'
  );
}

const DISPLAY_OPTION_VALUE_SCHEMAS = {
  teamAssets: DiplayOptionsTeamAssetSchema,
  odds: DisplayOptionsOddsFormatSchema,
  dateTimeFormat: DateTimeFormatSchema,
  penaltiesFormat: PenaltiesFormatSchema,
} as const;

function parseTemplateDisplayOptionValue(key: DisplayOptionsKey, value: unknown) {
  return DISPLAY_OPTION_VALUE_SCHEMAS[key].safeParse(value);
}

function elementExists(state: Record<string, unknown>, elementId: string): boolean {
  return (
    Array.isArray(state.elements) &&
    state.elements.some((element) => isRecord(element) && element.id === elementId)
  );
}

const FONT_FAMILY_FIELDS = ['fontWeight', 'fontStyle', 'fontStretch', 'fontVariant'] as const;

function mergeElementData(
  current: unknown,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const base = isRecord(current) ? current : {};
  const next: Record<string, unknown> = { ...base };
  for (const [key, value] of Object.entries(patch)) {
    const existing = base[key];
    if (isRecord(value) && isRecord(existing)) {
      next[key] = { ...existing, ...value };
      continue;
    }
    next[key] = value;
  }
  return next;
}

function fontFamilyPatchError(currentData: unknown, patch: Record<string, unknown>): string | null {
  if (!isRecord(patch.style) || !Object.prototype.hasOwnProperty.call(patch.style, 'fontFamily')) {
    return null;
  }
  const currentStyle =
    isRecord(currentData) && isRecord(currentData.style) ? currentData.style : {};
  if (patch.style.fontFamily === currentStyle.fontFamily) {
    return null;
  }
  const missing = FONT_FAMILY_FIELDS.filter(
    (field) => !Object.prototype.hasOwnProperty.call(patch.style, field),
  );
  if (missing.length === 0) {
    return null;
  }
  return `A fontFamily change must also include ${missing.join(', ')}.`;
}

async function runOperationCore(
  invocationId: string,
  context: TemplateOperationContext,
  operation: TemplateBuilderOperation,
  input: Record<string, unknown>,
): Promise<TemplateOperationResult> {
  if (!operationSupported(operation)) {
    return operationError(
      invocationId,
      'UNSUPPORTED',
      `${operation} is not available on Template Builder WebMCP.`,
    );
  }

  const state = context.store?.getState();
  if (!context.store || !state || !Array.isArray(state.elements)) {
    return operationError(
      invocationId,
      'EDITOR_NOT_READY',
      'The Template Builder editor is not ready.',
    );
  }

  const actions = state as Record<string, unknown> & {
    setName: (name: string) => void;
    setBgColour: (colour: string) => void;
    setBusinessEntityId: (id: number) => void;
    setCustomFonts: (fonts: Array<{ fontFamily: string; src?: string }>) => void;
    setBrandKitId: (id: string | null) => void;
    setBrandKit: (brandKit: unknown) => void;
    setElementSelected: (id: string | null) => void;
    updateElementName: (id: string, name: string) => void;
    updateElementPosition: (id: string, x: number, y: number) => void;
    reorderElement: (id: string, command: ElementReorderCommand) => void;
    removeElement: (id: string) => void;
    addElement: (element: AdElement) => void;
    updateElementData: (id: string, data: AdElement['data']) => void;
    updateElementLocked: (id: string, locked: boolean) => void;
    updateElementHidden: (id: string, hidden: boolean) => void;
    updateElementDataSources: (
      id: string,
      sources: Array<{ sourceId: string; name: string }>,
    ) => void;
    removeElementDataSource: (id: string, dataSourceId: string) => void;
    addDataSource: (
      type: Datasources.SourceType,
      dataType?: Datasources.DataType,
      id?: string,
    ) => void;
    removeDataSource: (id: string) => void;
    setDisplayOptions: (key: string, value: unknown) => void;
    setElementDateDisplayOptions: (id: string, value: unknown) => void;
    setElementOddsDisplayOptions: (id: string, value: unknown) => void;
    setElementPenaltiesFormat: (id: string, value: unknown) => void;
  };

  const requireElement = (elementId: unknown): TemplateOperationResult | null => {
    if (typeof elementId !== 'string' || !elementExists(state, elementId)) {
      return operationError(
        invocationId,
        'INVALID_INPUT',
        'That element is not on the open template.',
      );
    }
    return null;
  };

  switch (operation) {
    case 'set_name': {
      if (typeof input.name !== 'string') {
        return operationError(invocationId, 'INVALID_INPUT', 'set_name requires name.');
      }
      actions.setName(input.name);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'set_background': {
      if (typeof input.colour !== 'string') {
        return operationError(invocationId, 'INVALID_INPUT', 'set_background requires colour.');
      }
      actions.setBgColour(input.colour);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'set_business_entity': {
      if (typeof input.businessEntityId !== 'number') {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'set_business_entity requires businessEntityId.',
        );
      }
      const routeMode = context.routeMode ?? 'edit';
      if (routeMode === 'edit') {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'set_business_entity is only available on create and duplicate.',
        );
      }
      if (!context.listAdvertisers) {
        return operationError(
          invocationId,
          'EDITOR_NOT_READY',
          'The advertiser list is not available.',
        );
      }
      const advertisers = await context.listAdvertisers();
      const businessEntityId = input.businessEntityId;
      const knownAdvertiser = advertisers.some(
        (row) => advertiserRecordId(row) === businessEntityId,
      );
      if (!knownAdvertiser) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'set_business_entity received an unknown businessEntityId.',
        );
      }
      if (state.brand == null && context.loadAdvertiserFonts) {
        try {
          const fonts = await context.loadAdvertiserFonts(businessEntityId);
          actions.setCustomFonts(fonts);
        } catch (error) {
          return operationError(
            invocationId,
            'UNEXPECTED',
            'Advertiser fonts could not be loaded.',
            {
              message: error instanceof Error ? error.message : String(error),
            },
          );
        }
      }
      actions.setBusinessEntityId(businessEntityId);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'set_brand_kit': {
      if (input.brandKitId !== null && typeof input.brandKitId !== 'string') {
        return operationError(invocationId, 'INVALID_INPUT', 'set_brand_kit requires brandKitId.');
      }
      actions.setBrandKitId(input.brandKitId);
      if ('brandKit' in input) {
        actions.setBrandKit(input.brandKit ?? null);
      }
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'select_element': {
      if (input.elementId !== null && typeof input.elementId !== 'string') {
        return operationError(invocationId, 'INVALID_INPUT', 'select_element requires elementId.');
      }
      if (typeof input.elementId === 'string') {
        const missing = requireElement(input.elementId);
        if (missing) return missing;
      }
      actions.setElementSelected(input.elementId);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'rename_element': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (typeof input.name !== 'string') {
        return operationError(invocationId, 'INVALID_INPUT', 'rename_element requires name.');
      }
      actions.updateElementName(input.elementId as string, input.name);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'move_element': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (typeof input.x !== 'number' || typeof input.y !== 'number') {
        return operationError(invocationId, 'INVALID_INPUT', 'move_element requires x and y.');
      }
      actions.updateElementPosition(input.elementId as string, input.x, input.y);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'reorder_element': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      const command = input.command;
      if (
        command !== 'front' &&
        command !== 'forward' &&
        command !== 'backward' &&
        command !== 'back'
      ) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'reorder_element requires command front, forward, backward, or back.',
        );
      }
      const target = (state.elements as Array<{ id?: string; type?: string; name?: string }>).find(
        (element) => element.id === input.elementId,
      );
      if (target && isPinnedBackgroundLayer(target)) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'The template background layer cannot be reordered.',
        );
      }
      actions.reorderElement(input.elementId as string, command);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'remove_element': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      const current = (state.elements as Array<{ id?: string; type?: string }>).find(
        (element) => element.id === input.elementId,
      );
      if (context.source === 'webmcp' && current?.type === 'WIDGET') {
        return operationError(invocationId, 'INVALID_INPUT', WIDGET_LAYER_MESSAGE);
      }
      actions.removeElement(input.elementId as string);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'add_element': {
      const parsed = AdElementSchema.safeParse(input.element);
      if (!parsed.success) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'add_element requires a valid element.',
        );
      }
      if (context.source === 'webmcp' && parsed.data.type === 'WIDGET') {
        return operationError(invocationId, 'INVALID_INPUT', WIDGET_LAYER_MESSAGE);
      }
      actions.addElement(parsed.data);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'update_element_data': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (!isRecord(input.data)) {
        return operationError(invocationId, 'INVALID_INPUT', 'update_element_data requires data.');
      }
      const current = (
        state.elements as Array<{ id?: string; type?: string; data?: unknown }>
      ).find((element) => element.id === input.elementId);
      if (context.source === 'webmcp' && current?.type === 'WIDGET') {
        return operationError(invocationId, 'INVALID_INPUT', WIDGET_LAYER_MESSAGE);
      }
      const fontError = fontFamilyPatchError(current?.data, input.data);
      if (fontError) {
        return operationError(invocationId, 'INVALID_INPUT', fontError);
      }
      if (
        current?.type === 'IMAGE' &&
        isRecord(input.data) &&
        Object.prototype.hasOwnProperty.call(input.data, 'content')
      ) {
        const imageContentError = validateImageContentWrite(input.data.content);
        if (imageContentError) {
          return operationError(invocationId, 'INVALID_INPUT', imageContentError);
        }
      }
      const parsed = AdElementSchema.safeParse({
        ...current,
        data: mergeElementData(current?.data, input.data),
      });
      if (!parsed.success) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'update_element_data received an invalid element.',
        );
      }
      actions.updateElementData(input.elementId as string, parsed.data.data);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'set_element_sources': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (!Array.isArray(input.sources)) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'set_element_sources requires sources.',
        );
      }
      const parsedSources = AdElementDataSourceSchema.array().safeParse(input.sources);
      if (!parsedSources.success) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'set_element_sources received invalid sources.',
        );
      }
      actions.updateElementDataSources(input.elementId as string, parsedSources.data);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'remove_element_source': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (typeof input.dataSourceId !== 'string') {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'remove_element_source requires dataSourceId.',
        );
      }
      actions.removeElementDataSource(input.elementId as string, input.dataSourceId);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'add_data_source': {
      const parsedType = Datasources.SourceTypeSchema.safeParse(input.type);
      if (!parsedType.success) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'add_data_source received an invalid type.',
        );
      }
      let dataType: Datasources.DataType | undefined;
      if (input.dataType !== undefined && input.dataType !== null) {
        const parsedDataType = Datasources.DataTypeSchema.safeParse(input.dataType);
        if (!parsedDataType.success) {
          return operationError(
            invocationId,
            'INVALID_INPUT',
            'add_data_source received an invalid dataType.',
          );
        }
        dataType = parsedDataType.data;
      }
      if (input.id !== undefined && input.id !== null && typeof input.id !== 'string') {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'add_data_source id must be a string.',
        );
      }
      const requestedId = typeof input.id === 'string' ? input.id : undefined;
      if (
        requestedId &&
        Array.isArray(state.dataSources) &&
        state.dataSources.some((source) => isRecord(source) && source.id === requestedId)
      ) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'add_data_source id already exists on this template.',
        );
      }
      actions.addDataSource(parsedType.data, dataType, requestedId);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'remove_data_source': {
      if (typeof input.dataSourceId !== 'string') {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'remove_data_source requires dataSourceId.',
        );
      }
      actions.removeDataSource(input.dataSourceId);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'set_display_options': {
      if (!isDisplayOptionsKey(input.key)) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'set_display_options requires a known key.',
        );
      }
      const parsedDisplay = parseTemplateDisplayOptionValue(input.key, input.value);
      if (!parsedDisplay.success) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'set_display_options received an invalid value.',
        );
      }
      actions.setDisplayOptions(input.key, parsedDisplay.data);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'set_element_date_format':
    case 'set_element_odds_format':
    case 'set_element_penalties_format': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      const displayKey = ELEMENT_FORMAT_DISPLAY_KEY[operation];
      const parsed = parseTemplateDisplayOptionValue(displayKey, input.value);
      if (!parsed.success) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          `${operation} received an invalid value.`,
        );
      }
      if (operation === 'set_element_date_format') {
        actions.setElementDateDisplayOptions(input.elementId as string, parsed.data);
      } else if (operation === 'set_element_odds_format') {
        actions.setElementOddsDisplayOptions(input.elementId as string, parsed.data);
      } else {
        actions.setElementPenaltiesFormat(input.elementId as string, parsed.data);
      }
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'stage_saved_version': {
      if (!context.store) {
        return operationError(
          invocationId,
          'EDITOR_NOT_READY',
          'The Template Builder editor is not ready.',
        );
      }
      if (!context.redeemSavedVersionRestore) {
        return operationError(
          invocationId,
          'EDITOR_NOT_READY',
          'Saved version restore is not available.',
        );
      }
      const restoreToken = input.restoreToken;
      if (typeof restoreToken !== 'string' || restoreToken.length === 0) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'stage_saved_version requires restoreToken.',
        );
      }
      if (
        typeof input.expectedWorkingRevision !== 'number' ||
        !Number.isFinite(input.expectedWorkingRevision) ||
        typeof input.expectedPersistedRevision !== 'number' ||
        !Number.isFinite(input.expectedPersistedRevision)
      ) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'stage_saved_version requires expectedWorkingRevision and expectedPersistedRevision.',
        );
      }
      const currentWorkingRevision = readWorkingRevision(context.store.getState());
      const currentPersistedRevision = readPersistedRevision(context.store.getState());
      if (input.expectedWorkingRevision !== currentWorkingRevision) {
        return operationError(
          invocationId,
          'STALE_REVISION',
          'The working copy changed since preview. Read the document and preview the version again.',
        );
      }
      if (input.expectedPersistedRevision !== currentPersistedRevision) {
        return operationError(
          invocationId,
          'STALE_REVISION',
          'The persisted template changed since preview. Read the document and preview the version again.',
        );
      }
      try {
        const redeemed = await context.redeemSavedVersionRestore({
          restoreToken,
          expectedWorkingRevision: input.expectedWorkingRevision,
          expectedPersistedRevision: input.expectedPersistedRevision,
        });
        applyAdTemplateToEditorStore(context.store, redeemed.adTemplate as AdTemplate);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Could not restore the saved version.';
        if (message.toLowerCase().includes('stale') || message.toLowerCase().includes('expired')) {
          return operationError(invocationId, 'STALE_REVISION', message);
        }
        return operationError(invocationId, 'INVALID_TOKEN', message);
      }
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'commit_working_copy': {
      if (
        typeof input.expectedWorkingRevision !== 'number' ||
        !Number.isFinite(input.expectedWorkingRevision)
      ) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'commit_working_copy requires expectedWorkingRevision.',
        );
      }
      if (input.userConfirmed != null && typeof input.userConfirmed !== 'boolean') {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'commit_working_copy userConfirmed must be a boolean.',
        );
      }
      if (input.cancel != null && typeof input.cancel !== 'boolean') {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'commit_working_copy cancel must be a boolean.',
        );
      }
      if (input.userConfirmed === true && input.cancel === true) {
        return operationError(
          invocationId,
          'INVALID_INPUT',
          'commit_working_copy cannot confirm and cancel together.',
        );
      }
      const settlement = await requestTemplateSave({
        expectedWorkingRevision: input.expectedWorkingRevision,
        saveInvocationId: invocationId,
        saveSource: context.source,
        userConfirmed: input.userConfirmed === true,
        cancel: input.cancel === true,
        signal: context.signal,
      });
      if (context.signal?.aborted) {
        return operationError(invocationId, 'UNEXPECTED', 'The tool invocation was cancelled.');
      }
      if (settlement.status === 'awaiting_confirmation') {
        return operationSuccess(invocationId, operation, context.store.getState(), {
          saved: false,
          settlementStatus: 'awaiting_confirmation',
        });
      }
      if (settlement.status === 'saved') {
        return operationSuccess(invocationId, operation, context.store.getState(), {
          saved: true,
          settlementStatus: 'saved',
        });
      }
      if (settlement.status === 'cancelled') {
        return operationSuccess(invocationId, operation, context.store.getState(), {
          saved: false,
          settlementStatus: 'cancelled',
        });
      }
      if (settlement.status === 'blocked') {
        return operationError(invocationId, 'INVALID_INPUT', settlement.message);
      }
      if (settlement.status === 'stale') {
        return operationError(
          invocationId,
          'STALE_REVISION',
          'The working copy changed before save. Read the document again.',
        );
      }
      return operationError(invocationId, 'UNEXPECTED', 'Template Builder save failed.', {
        message: settlement.message,
      });
    }
    case 'set_element_locked': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (typeof input.locked !== 'boolean') {
        return operationError(invocationId, 'INVALID_INPUT', 'set_element_locked requires locked.');
      }
      actions.updateElementLocked(input.elementId as string, input.locked);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    case 'set_element_hidden': {
      const missing = requireElement(input.elementId);
      if (missing) return missing;
      if (typeof input.hidden !== 'boolean') {
        return operationError(invocationId, 'INVALID_INPUT', 'set_element_hidden requires hidden.');
      }
      actions.updateElementHidden(input.elementId as string, input.hidden);
      return operationSuccess(invocationId, operation, context.store.getState());
    }
    default:
      return operationError(invocationId, 'INVALID_INPUT', 'Unknown apply operation.');
  }
}

function operationError(
  invocationId: string,
  code: TemplateOperationErrorCode,
  message: string,
  details?: Record<string, unknown>,
): TemplateOperationResult {
  return {
    ok: false,
    invocationId,
    error: { code, message, ...(details ? { details } : {}) },
  };
}

function operationSuccess(
  invocationId: string,
  operation: string,
  state: Record<string, unknown>,
  extra?: Partial<TemplateOperationSuccessData>,
): TemplateOperationResult {
  return {
    ok: true,
    invocationId,
    data: {
      operation,
      workingRevision: readWorkingRevision(state),
      ...extra,
    },
  };
}

export async function executeTemplateBuilderOperation(
  context: TemplateOperationContext,
  input: ExecuteTemplateBuilderOperationInput,
  invocationId = createOperationInvocationId(),
): Promise<TemplateOperationResult> {
  const startedAt = Date.now();
  const result = await runOperationCore(invocationId, context, input.operation, input.args);
  dispatchTemplateOperationAudit(context, input.operation, input.args, startedAt, result);
  return result;
}

export type { TemplateBuilderOperation } from './names';
export {
  TEMPLATE_BUILDER_OPERATIONS,
  WORKING_COPY_OPERATIONS,
  RESTORE_OPERATIONS,
  SAVE_OPERATIONS,
  UNSUPPORTED_OPERATIONS,
  isTemplateBuilderOperation,
  operationSummary,
  operationSupported,
} from './names';
export type {
  TemplateOperationContext,
  TemplateOperationResult,
  TemplateOperationSource,
} from './types';
export { createOperationInvocationId } from './invocation';
export { readPersistedRevision, readWorkingRevision } from './revision';
